import { zodResolver } from "@hookform/resolvers/zod";
import type { SubmitBreakdownReportMutationRequest } from "@packages/api-client/types/equipmentController";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useSubmitBreakdownReport } from "@/api/equipment";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormRootMessage,
	useGuardedSubmit,
} from "@/components/ui/form";
import { FormActionBar } from "@/components/ui/form-action-bar";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { SubmitButton } from "@/components/ui/submit-button";
import { Textarea } from "@/components/ui/textarea";
import { useEquipmentListData } from "@/hooks/useEquipmentListData";
import type { FarmScope } from "@/hooks/useFarmScope";
import { requiredString } from "@/utils/formSchemas";
import { cn } from "@/utils/utils";

type SearchParams = { equipmentId?: string; farm?: string };

// The equipment selector is deliberately org-wide regardless of the selected
// farm: a broken machine may be unassigned (no location) or parked at another
// farm, and hiding it would block the report.
const ALL_FARMS: FarmScope = { kind: "all", establishmentIds: [] };

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/equipment/breakdown-report/",
)({
	// Queueable append-only field event (roadmap Tier 1 item 5): breakdowns
	// happen precisely where there's no signal. Equipment options come from the
	// prepared fleet dataset offline; unprepared offline degrades to an empty
	// selector.
	staticData: { offline: "ready" },
	validateSearch: (search: Record<string, unknown>): SearchParams => ({
		equipmentId: search.equipmentId as string | undefined,
		// Preserve the org-level farm scope param across this route's validation.
		farm: typeof search.farm === "string" ? search.farm : undefined,
	}),
	component: BreakdownReportPage,
});

const SEVERITY_OPTIONS = [
	{ value: "CRITICAL", color: "border-error bg-error/5 text-error" },
	{ value: "HIGH", color: "border-warning bg-warning/5 text-warning" },
	{ value: "MEDIUM", color: "border-info bg-info/5 text-info" },
	{ value: "LOW", color: "border-base-300 bg-base-100 text-base-content" },
] as const;

type BreakdownFormData = {
	equipmentId: string;
	severity: string;
	description: string;
	locationOnMachine: string;
};

const breakdownSchema = z.object({
	equipmentId: requiredString(),
	severity: requiredString(),
	description: z.string(),
	locationOnMachine: z.string(),
});

const LOCATION_OPTIONS = [
	"ENGINE",
	"TRANSMISSION",
	"HYDRAULICS",
	"ELECTRICAL",
	"TIRES_WHEELS",
	"BODY_FRAME",
	"IMPLEMENT",
	"OTHER",
] as const;

function BreakdownReportPage() {
	const { orgId } = Route.useParams();
	const { equipmentId: prefilledEquipmentId } = Route.useSearch();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();

	const { rows: equipmentOptions } = useEquipmentListData({
		orgId,
		scope: ALL_FARMS,
	});

	const submitMutation = useSubmitBreakdownReport();

	const form = useForm<BreakdownFormData>({
		resolver: zodResolver(breakdownSchema),
		mode: "onTouched",
		reValidateMode: "onChange",
		defaultValues: {
			equipmentId: prefilledEquipmentId ?? "",
			severity: "",
			description: "",
			locationOnMachine: "",
		},
	});

	const onValid = (data: BreakdownFormData) => {
		const payload: SubmitBreakdownReportMutationRequest = {
			equipmentId: data.equipmentId,
			severity: data.severity,
			description: data.description || undefined,
			locationOnMachine: data.locationOnMachine || undefined,
		};

		return submitMutation
			.mutateAsync({ organizationId: orgId, data: payload })
			.then(() => {
				navigate({ to: "/app/org/$orgId/equipment", params: { orgId } });
			});
	};

	const guardedSubmit = useGuardedSubmit(form, onValid);

	return (
		<div className="flex flex-col gap-4 max-w-lg mx-auto w-full">
			<PageHeader title={t("equipment.breakdown.title")} showBackButton />

			<Form {...form}>
				<form onSubmit={guardedSubmit} className="space-y-4">
					{/* Equipment Selection */}
					<Card>
						<CardContent className="py-4">
							<FormField
								control={form.control}
								name="equipmentId"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>
											{t("equipment.breakdown.selectEquipment")}
										</FormLabel>
										<Select value={field.value} onValueChange={field.onChange}>
											<FormControl>
												<SelectTrigger>
													<SelectValue
														placeholder={t(
															"equipment.breakdown.selectEquipmentPlaceholder",
														)}
													/>
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{equipmentOptions.map((eq) => (
													<SelectItem key={eq.id} value={eq.id}>
														{eq.assetId} - {eq.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>
						</CardContent>
					</Card>

					{/* Severity Selection */}
					<Card>
						<CardContent className="py-4">
							<FormField
								control={form.control}
								name="severity"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>
											{t("equipment.breakdown.severity")}
										</FormLabel>
										<div className="grid grid-cols-2 gap-2">
											{SEVERITY_OPTIONS.map((option) => (
												<button
													key={option.value}
													type="button"
													onClick={() => field.onChange(option.value)}
													className={cn(
														"p-3 rounded-lg border-2 text-center transition-all",
														field.value === option.value
															? option.color
															: "border-base-300 text-base-content/60 hover:border-base-400",
													)}
												>
													<div className="font-medium text-sm">
														{t(
															`equipment.breakdown.severityOptions.${option.value}`,
														)}
													</div>
												</button>
											))}
										</div>
										<FormMessage />
									</FormItem>
								)}
							/>
						</CardContent>
					</Card>

					{/* Description */}
					<Card>
						<CardContent className="py-4 space-y-3">
							<FormField
								control={form.control}
								name="description"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t("equipment.breakdown.description")}
										</FormLabel>
										<FormControl>
											<Textarea
												placeholder={t(
													"equipment.breakdown.descriptionPlaceholder",
												)}
												rows={4}
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="locationOnMachine"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t("equipment.breakdown.locationOnMachine")}
										</FormLabel>
										<Select value={field.value} onValueChange={field.onChange}>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder={t("common.select")} />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{LOCATION_OPTIONS.map((loc) => (
													<SelectItem key={loc} value={loc}>
														{t(`equipment.breakdown.locations.${loc}`)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>
						</CardContent>
					</Card>

					<FormRootMessage />

					{/* Submit */}
					<FormActionBar>
						<SubmitButton
							pending={submitMutation.isPending}
							size="lg"
							variant="destructive"
						>
							<AlertTriangle className="h-4 w-4 mr-2" />
							{t("equipment.breakdown.submit")}
						</SubmitButton>
					</FormActionBar>
				</form>
			</Form>
		</div>
	);
}
